import {
	ChangeEvent,
	FunctionComponent,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react";
import { useTranslation } from "react-i18next";
import useLocalStorageState from "use-local-storage-state";

import {
	faColumns3,
	faMegaphone,
	faRadar,
	faRotate,
	faSliders,
} from "@fortawesome/pro-regular-svg-icons";

import { Table, TableColumn } from "@/components/Table/Table";
import { applyPersistedColumnConfig } from "@/components/Table/applyPersistedColumnConfig";
import { NoResultsFilterWarning } from "@/components/NoResultsFilterWarning/NoResultsFilterWarning";
import { TItem } from "@/components/SortableList/SortableList";
import { Button } from "@/components/Button/Button";
import { ButtonColor, ButtonSize } from "@/components/Button/types";
import { Icon, Modal } from "@/components";
import { useAddToListConfirm } from "@/components/AddToListConfirm/useAddToListConfirm";
import { formatCappedCount } from "@/utils/formatCappedCount";
import { InputField } from "@/components/InputField/InputField";
import { Radio } from "@/components/Radio/Radio";
import { AutoComplete, AutoCompleteItem } from "@/components/AutoComplete/AutoComplete";
import { Order, SortParams } from "@/types/types";
import { useCopyToClipboard } from "@/utils/useCopyToClipboard";
import { useSnackBar } from "@/utils/useSnackbar";
import { useSearch } from "@/utils";
import { useAppDispatch, useAppSelector } from "@/redux/hooks";
import { COLUMN_CONFIG_VERSION } from "@/constants";
import { ListSourceType, SmartListFrequency } from "@/enum/list.enum";
import { useIsFeatureActive } from "@/utils/useIsFeatureActive";

import { AddToCrmModal, AddToCrmOwner } from "@/pages/Private/components/AddToCrm/AddToCrmModal";
import {
	useContactAddToBlacklistMutation,
	useLazyGetContactsQuery,
	useContactAddToListMutation,
	useContactAddToTrackerMutation,
	useLazyGetContactProspectDetailQuery,
} from "@/pages/Private/redux/contact/contact.api";
import { useAddProspectsToCrmMutation } from "@/pages/Private/redux/crm/crm.api";
import {
	CrmEntityType,
	CrmProvider,
	CrmSelectionSource,
} from "@/pages/Private/redux/crm/crm.schema";

import { renderListMeta } from "@/utils/listMeta";

import { Checkbox } from "@/components/Checkbox/Checkbox";

import { BlacklistConfirmModal } from "./BlacklistConfirmModal";

import { FeaturesCodes } from "../../Users/enum/codes";

import { useCreateSmartListMutation } from "../../../redux/list/list.api";
import { useTrackerDestinationLists } from "../../List/utils/useTrackerDestinationLists";
import { contactSelector, openContactLeadPanel } from "../../../redux/contact/contact.slice";
import { ContactSelectType } from "../schema/contacts";
import { ContactsProspectFilters } from "../constants/contacts-filter";
import { useContactsExport } from "../hooks/useContactsExport";
import { useContactsUrlState } from "../hooks/useContactsUrlState";
import { ContactsTableColumns } from "./TableColumnConfigs/ContactsTableColumns";
import { ContactsColumnModal } from "./ContactsColumnModal";
import { ContactsFilterModal } from "./ContactsFilterModal";
import { ContactsActionBar } from "./ContactsActionBar";

import { ContactLeadPanel } from "./ContactLeadPanel";
import { SmartListFooterBar } from "./SmartListFooterBar";

import { CreateSmartListModal } from "./CreateSmartListModal";
import { useContactLookups } from "./useContactLookups";

export const ContactsTable: FunctionComponent = () => {
	const { t } = useTranslation();
	const ts = useCallback((key: string) => t(`contacts.${key}`), [t]);
	const isSmartListActive = useIsFeatureActive(FeaturesCodes.SMART_LIST);
	const dispatch = useAppDispatch();

	const {
		page,
		setPage,
		limit,
		setLimit,
		sortingInbox,
		setSortingInbox,
		filter,
		setFilter,
		applyFilters,
		searchValue,
		setSearchValue,
		query,
		semanticSearchProspects,
		setSemanticSearchProspects,
	} = useContactsUrlState({
		prefix: "contacts",
		defaultSortBy: "prospect.updatedAt",
		defaultSortOrder: Order.DESC,
		withSearch: true,
	}) as ReturnType<typeof useContactsUrlState> & {
		searchValue: string;
		setSearchValue: (v: string) => void;
		query: string;
		isTyping: boolean;
	};

	const [showEdit, setShowEdit] = useState<{ id: number; name: string } | undefined>(undefined);
	const [showFilterModal, setShowFilterModal] = useState(false);
	const [showSelectColumns, setShowSelectColumns] = useState(false);
	const [tableConfig, setTableConfig] = useLocalStorageState<TItem[]>(
		"contacts-columns-prospects",
		{
			defaultValue: [],
		}
	);
	// Force-migrates a saved column config to the current default layout when the layout version is
	// bumped (PREC-3567).
	const [columnConfigVersion, setColumnConfigVersion] = useLocalStorageState<number>(
		"contacts-columns-prospects-version",
		{ defaultValue: 0 }
	);

	const [selectedItems, setSelectedItems] = useState<number[]>([]);
	const [selectAll, setSelectAll] = useState(false);
	const [showAll, setShowAll] = useState(false);
	const [selectedCrmProvider, setSelectedCrmProvider] = useState<CrmProvider>();
	const [selectType, setSelectType] = useState<ContactSelectType>(ContactSelectType.CURRENT_PAGE);
	const [prepSelectType, setPrepSelectType] = useState<ContactSelectType>(ContactSelectType.ALL);

	const { renderSnackbar, handleCopyClick } = useCopyToClipboard();
	const { handleOpenSnackbar, renderMessageSnackbar } = useSnackBar();

	const { contacts: data, contactsMeta: meta } = useAppSelector(contactSelector);

	const [triggerGetContacts, { isFetching }] = useLazyGetContactsQuery();
	const [triggerGetContactProspectDetail] = useLazyGetContactProspectDetailQuery();
	const lastQueryPromise = useRef<ReturnType<typeof triggerGetContacts> | null>(null);
	const lastDetailQueryPromise = useRef<ReturnType<typeof triggerGetContactProspectDetail> | null>(
		null
	);

	useEffect(() => {
		lastQueryPromise.current?.abort();
		lastQueryPromise.current = triggerGetContacts({
			page,
			limit,
			searchValue: query || undefined,
			sortBy: sortingInbox.sortBy,
			sortOrder: sortingInbox.sortOrder,
			...(filter && Object.keys(filter).length > 0 && { filter: JSON.stringify(filter) }),
			...(semanticSearchProspects && { semanticSearchProspects }),
		});
	}, [
		page,
		limit,
		query,
		sortingInbox.sortBy,
		sortingInbox.sortOrder,
		filter,
		semanticSearchProspects,
		triggerGetContacts,
	]);

	const [addToList, { isLoading: isAddToListLoading }] = useContactAddToListMutation();
	const [addToTracker, { isLoading: isAddToTrackerLoading }] = useContactAddToTrackerMutation();
	const [addProspectsToCrm, { isLoading: isAddToCrmLoading }] = useAddProspectsToCrmMutation();

	// Smart List state
	const [showSmartListModal, setShowSmartListModal] = useState(false);
	const [createSmartList, { isLoading: isCreateSmartListLoading }] = useCreateSmartListMutation();

	// Blacklist state
	const [showBlacklistModal, setShowBlacklistModal] = useState(false);
	const [addEntireCompanyToBlacklist, setAddEntireCompanyToBlacklist] = useState(false);
	const [addToBlacklist, { isLoading: isAddToBlacklistLoading }] =
		useContactAddToBlacklistMutation();

	const filterCount = filter ? Object.keys(filter).length : 0;
	const hasSemanticSearch = !!semanticSearchProspects;
	const showSmartListFooter = selectedItems.length !== 0 && (filterCount > 0 || hasSemanticSearch);

	// When a search query returns no rows while modal filters are active, hint that the
	// filters may be the reason and offer to re-run the search by query alone.
	const showFilterWarning = !!query && (filterCount > 0 || hasSemanticSearch);
	const handleSearchWithoutFilters = () =>
		applyFilters({
			filter: undefined,
			semanticSearchProspects: "",
			semanticSearchOrganizations: "",
		});

	// Tracker state
	const [showAddToTrackerModal, setShowAddToTrackerModal] = useState(false);
	const [selectedTracker, setSelectedTracker] = useState<AutoCompleteItem | undefined>(undefined);
	const [singleTrackerProspectId, setSingleTrackerProspectId] = useState<number | null>(null);
	const { setSearchValue: setTrackerSearchValue, query: trackerQuery } = useSearch();

	const [addOrganizationOnly, setAddOrganizationOnly] = useState<boolean>(false);

	const {
		lists: combinedTrackerAndJobChangeLists,
		isLoading: isLoadingTrackerLists,
		isFetching: isFetchingTrackerLists,
	} = useTrackerDestinationLists({ searchValue: trackerQuery });

	const selectedListIsJobChange =
		combinedTrackerAndJobChangeLists.find((list) => list.id === selectedTracker?.id)?.sourceType ===
		ListSourceType.JOB_CHANGE;

	const getSelectionPayload = useCallback(() => {
		if (selectType === ContactSelectType.ALL) {
			return {
				selectType: ContactSelectType.ALL,
				...(filter && Object.keys(filter).length > 0 && { filter: JSON.stringify(filter) }),
				...(query && { searchValue: query }),
				...(semanticSearchProspects && { semanticSearchProspects }),
			};
		}

		return {
			selectType: ContactSelectType.CURRENT_PAGE,
			ids: selectedItems,
		};
	}, [selectType, selectedItems, filter, query, semanticSearchProspects]);

	// How many contacts the current selection addresses. Under ALL that is the server's count,
	// which may be capped — so it is a floor, and every label built from it says "N+".
	const selectionCount =
		selectType === ContactSelectType.ALL ? (meta?.itemCount ?? 0) : selectedItems.length;

	const toggleSelectAll = useCallback(() => {
		if (selectAll) {
			setSelectedItems([]);
			setSelectAll(false);
			setSelectType(ContactSelectType.CURRENT_PAGE);
		} else {
			const allIds = data?.map((p) => p.id) || [];

			setSelectedItems(allIds);
			setSelectAll(true);
		}
	}, [selectAll, data]);

	const handleSetSelectType = useCallback(
		(type: ContactSelectType) => {
			const allIds = data?.map((p) => p.id) || [];

			setSelectedItems(allIds);
			setSelectAll(true);
			setShowAll(false);
			setSelectType(type);
			setPrepSelectType(ContactSelectType.ALL);
		},
		[data]
	);

	const handleSelectItem = useCallback((id: number) => {
		setSelectedItems((prev) => (prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]));
		setSelectAll(false);
		setSelectType(ContactSelectType.CURRENT_PAGE);
	}, []);

	const addToListConfirm = useAddToListConfirm({
		selection: { count: selectionCount, source: "contacts" },
		send: (target) => addToList({ ...getSelectionPayload(), ...target }).unwrap(),
		showMessage: handleOpenSnackbar,
	});

	const lookups = useContactLookups({ selectedItems, rows: data, notify: handleOpenSnackbar });

	const { handleExport, isExportLoading } = useContactsExport({
		selectType,
		selectedItems,
		filter,
		query,
		semanticSearchProspects,
		openSnackbar: handleOpenSnackbar,
	});

	const handleOpenTrackerModal = useCallback((prospectId?: number) => {
		if (prospectId) {
			setSingleTrackerProspectId(prospectId);
		}

		setShowAddToTrackerModal(true);
	}, []);

	const handleConfirmAddToTracker = useCallback(async () => {
		if (!selectedTracker) {
			return;
		}

		try {
			let payload;

			if (singleTrackerProspectId) {
				payload = {
					selectType: ContactSelectType.CURRENT_PAGE,
					ids: [singleTrackerProspectId],
					listId: Number(selectedTracker.id),
					addOrganizationOnly: addOrganizationOnly && !selectedListIsJobChange,
				};
			} else {
				const selectionPayload = getSelectionPayload();

				payload = {
					...selectionPayload,
					listId: Number(selectedTracker.id),
					addOrganizationOnly: addOrganizationOnly && !selectedListIsJobChange,
				};
			}

			await addToTracker(payload).unwrap();
			handleOpenSnackbar(
				addOrganizationOnly ? t("contacts.addedCompanyToTracker") : t("contacts.addedToTracker")
			);
		} catch {
			handleOpenSnackbar(t("basics.error"));
		} finally {
			setShowAddToTrackerModal(false);
			setSingleTrackerProspectId(null);
			setSelectedTracker(undefined);
			setAddOrganizationOnly(false);
		}
	}, [
		selectedTracker,
		singleTrackerProspectId,
		addOrganizationOnly,
		selectedListIsJobChange,
		addToTracker,
		getSelectionPayload,
		handleOpenSnackbar,
		t,
	]);

	const handleSubmitAddToCrm = useCallback(
		async (provider: CrmProvider, entities: CrmEntityType[], owner?: AddToCrmOwner) => {
			try {
				await addProspectsToCrm({
					provider,
					entities,
					ownerId: owner?.id,
					ownerName: owner?.name,
					selection: {
						source: CrmSelectionSource.CONTACTS,
						...getSelectionPayload(),
					},
				}).unwrap();

				handleOpenSnackbar(t("crmAddTo.success"));
				setSelectedCrmProvider(undefined);
			} catch {
				handleOpenSnackbar(t("crmAddTo.error"));
			}
		},
		[addProspectsToCrm, getSelectionPayload, handleOpenSnackbar, t]
	);

	const handleCreateSmartList = useCallback(
		async (values: { name: string; frequency: SmartListFrequency }) => {
			try {
				await createSmartList({
					name: values.name,
					frequency: values.frequency,
					...(filter && Object.keys(filter).length > 0 && { filter: JSON.stringify(filter) }),
					...(semanticSearchProspects && { semanticSearchProspects }),
				}).unwrap();
				handleOpenSnackbar(t("contacts.smartList.created", { name: values.name }));
				setShowSmartListModal(false);
			} catch {
				handleOpenSnackbar(t("basics.error"));
			}
		},
		[createSmartList, filter, semanticSearchProspects, handleOpenSnackbar, t]
	);

	const handleAddToBlacklist = useCallback(async () => {
		try {
			const payload = getSelectionPayload();

			await addToBlacklist({
				...payload,
				addOrganization: addEntireCompanyToBlacklist,
			}).unwrap();

			handleOpenSnackbar(ts("addedToBlacklist"));
		} catch {
			handleOpenSnackbar(t("basics.error"));
		} finally {
			setShowBlacklistModal(false);
			setAddEntireCompanyToBlacklist(false);
		}
	}, [getSelectionPayload, addEntireCompanyToBlacklist, addToBlacklist, handleOpenSnackbar, ts, t]);

	const handleChangeRowsPerPage = (newLimit: number) => {
		setLimit(newLimit);
	};

	const handleSorting = (sortParams: SortParams) => {
		if (!sortParams.sortBy) {
			setSortingInbox({ sortBy: "prospect.updatedAt", sortOrder: Order.DESC });

			return;
		}

		setSortingInbox(sortParams);
	};

	const handleOpenLeadPanel = useCallback(
		(id: number, e?: React.MouseEvent<HTMLTableRowElement>) => {
			const target = e?.target as Element | null;

			if (target?.closest("button,a,input,label,select,textarea,[role='button']")) {
				return;
			}

			const prospect = data?.find((contact) => contact.id === id);

			if (prospect) {
				dispatch(openContactLeadPanel(prospect));
				lastDetailQueryPromise.current?.abort();
				lastDetailQueryPromise.current = triggerGetContactProspectDetail(prospect.id);
			}
		},
		[data, dispatch, triggerGetContactProspectDetail]
	);

	const columns = useMemo(() => {
		const cols = ContactsTableColumns({
			handleCopyClick,
			ts,
			sortingInbox,
			setShowEdit,
			showEdit,
			setShowAll,
			toggleSelectAll,
			selectAll,
			selectedItems,
			handleSelectItem,
			onAddToTracker: handleOpenTrackerModal,
		});

		return applyPersistedColumnConfig(cols, tableConfig);
	}, [
		handleCopyClick,
		ts,
		sortingInbox,
		showEdit,
		tableConfig,
		selectAll,
		selectedItems,
		toggleSelectAll,
		handleSelectItem,
		handleOpenTrackerModal,
	]);

	const resetTableConfig = (force = false) => {
		const sourceCols = force
			? ContactsTableColumns({
					handleCopyClick,
					ts,
					sortingInbox,
					setShowEdit,
					showEdit,
					setShowAll,
					toggleSelectAll,
					selectAll,
					selectedItems,
					handleSelectItem,
					onAddToTracker: handleOpenTrackerModal,
				})
			: columns;

		const newConfig: TItem[] = sourceCols.map((c: TableColumn, index: number) => ({
			uid: c.uid ?? "",
			id: index + 1,
			show: typeof c.show !== "undefined" ? c.show : true,
			content: c.title,
			locked: !!c.locked || false,
			allowExclude: typeof c.allowExclude !== "undefined" ? c.allowExclude : true,
			custom: false,
			moveable: c.moveable,
			hideable: c.hideable,
		}));

		if (tableConfig.length === 0 || force) {
			setTableConfig(newConfig);
		}
	};

	useMemo(() => {
		if (columns.length > 0 && tableConfig.length === 0) {
			resetTableConfig();
		}
	}, [columns]);

	// Force-reset the persisted column config to the new defaults once when the layout version is
	// bumped, so existing users adopt the improved default order/visibility (PREC-3567).
	useEffect(() => {
		if (columnConfigVersion !== COLUMN_CONFIG_VERSION) {
			resetTableConfig(true);
			setColumnConfigVersion(COLUMN_CONFIG_VERSION);
		}
	}, [columnConfigVersion]);

	return (
		<div className="w-full">
			<div className="flex items-center gap-2 h-11 px-2 bg-white border-b border-border-light">
				<div className="w-[280px] flex-shrink-0">
					<InputField
						className="!rounded-[10px]"
						classNameWrapper="!mb-0"
						handleChange={(e: ChangeEvent<HTMLInputElement>) => {
							setSearchValue(e.target.value);
						}}
						name="search"
						placeholder={ts("searchPlaceholder")}
						size={ButtonSize.XS}
						value={searchValue}
					/>
				</div>
				<div className="ml-auto flex flex-row items-center gap-2">
					{isSmartListActive && showSmartListFooter ? (
						<div className="w-fit">
							<Button
								onClick={() => setShowSmartListModal(true)}
								title={ts("smartList.createSmartList")}
								size={ButtonSize.S}
								image={<Icon className="mr-2" icon={faRotate} />}
								color={ButtonColor.ACTION}
								disabled={isCreateSmartListLoading}
							/>
						</div>
					) : null}
					<div className="w-fit">
						<Button
							color={ButtonColor.ACTION_SECONDARY}
							image={<Icon className="mr-2" icon={faColumns3} />}
							size={ButtonSize.S}
							title={ts("selectColumns")}
							onClick={() => setShowSelectColumns(true)}
						/>
					</div>
					<div className="w-fit">
						<Button
							color={ButtonColor.ACTION_SECONDARY}
							image={<Icon className="mr-2" icon={faSliders} />}
							size={ButtonSize.S}
							title={`${ts("filter")}${
								(filter && Object.keys(filter).length > 0) || semanticSearchProspects
									? ` (${(filter ? Object.keys(filter).length : 0) + (semanticSearchProspects ? 1 : 0)})`
									: ""
							}`}
							onClick={() => setShowFilterModal(true)}
						/>
					</div>
				</div>
			</div>

			<Table
				columns={columns}
				data={data || []}
				filter={filter}
				handleChangeRowsPerPage={handleChangeRowsPerPage}
				handleFetchPage={setPage}
				handleRowClick={handleOpenLeadPanel}
				handleSorting={handleSorting}
				isLoading={isFetching}
				limit={limit}
				noDataText={
					showFilterWarning ? (
						<NoResultsFilterWarning onSearchWithoutFilters={handleSearchWithoutFilters} />
					) : undefined
				}
				overallCount={meta?.itemCount}
				page={page}
				sticky
				tableConfig={tableConfig}
				totalPages={meta?.pageCount}
				selectedCount={selectedItems.length}
			/>

			<ContactLeadPanel />

			{showSelectColumns && (
				<ContactsColumnModal
					handleResetToDefault={() => {
						setTableConfig([]);
						setTimeout(() => resetTableConfig(true), 100);
					}}
					setShowSelectColumns={setShowSelectColumns}
					setTableConfig={setTableConfig}
					showSelectColumns={showSelectColumns}
					tableConfig={tableConfig}
				/>
			)}

			{showFilterModal && (
				<ContactsFilterModal
					applyFilters={applyFilters}
					filter={filter}
					filterDataType={ContactsProspectFilters}
					semanticSearchProspects={semanticSearchProspects}
					setFilter={setFilter}
					setSemanticSearchProspects={setSemanticSearchProspects}
					setShowFilterModal={setShowFilterModal}
					showFilterModal={showFilterModal}
				/>
			)}

			{showAll && (
				<Modal
					handleClose={() => setShowAll(false)}
					handleSave={async () => {
						handleSetSelectType(prepSelectType);
					}}
					isOpened={showAll}
					size="xs"
					submitButtonText={t("basics.confirm")}
					title={ts("select")}
				>
					<div className="flex flex-col items-start gap-4">
						<Radio
							isChecked={prepSelectType === ContactSelectType.ALL}
							onChange={() => {
								setPrepSelectType(ContactSelectType.ALL);
							}}
						>
							<>
								{ts("all")}{" "}
								<span className="text-xs bg-placeholder text-white ml-2 px-2 py-1 rounded-md">
									{formatCappedCount(meta?.itemCount, meta?.isCapped)}
								</span>
							</>
						</Radio>
						<Radio
							isChecked={prepSelectType === ContactSelectType.CURRENT_PAGE}
							onChange={() => {
								setPrepSelectType(ContactSelectType.CURRENT_PAGE);
							}}
						>
							<>
								{ts("currentPage")}{" "}
								<span className="text-xs bg-placeholder text-white ml-2 px-2 py-1 rounded-md">
									{meta?.itemCount && meta?.itemCount < limit ? meta?.itemCount : limit}
								</span>
							</>
						</Radio>
					</div>
				</Modal>
			)}

			<ContactsActionBar
				handleNavigateToPage={setPage}
				handleChangeRowsPerPage={handleChangeRowsPerPage}
				limit={limit}
				currentPage={page}
				totalPages={meta?.pageCount}
				columnsCount={columns.length}
				columnsShown={columns.filter((c) => c.show !== false).length}
				isAddToBlacklistLoading={isAddToBlacklistLoading}
				isAddToCrmLoading={isAddToCrmLoading}
				isAddToListLoading={isAddToListLoading}
				isAddToTrackerLoading={isAddToTrackerLoading}
				isEnrichEmailLoading={lookups.isEnrichEmailLoading}
				isEnrichPhoneLoading={lookups.isEnrichPhoneLoading}
				isExportLoading={isExportLoading}
				isCountCapped={meta?.isCapped}
				overallCount={meta?.itemCount}
				selectedCount={selectionCount}
				onAddToBlacklist={() => setShowBlacklistModal(true)}
				onAddToCrm={setSelectedCrmProvider}
				onAddToList={addToListConfirm.onSelectList}
				onAddToTracker={() => handleOpenTrackerModal()}
				onCreateAndAddToList={addToListConfirm.onCreateNew}
				onEnrichEmail={lookups.openEmail}
				onEnrichPhone={lookups.openPhone}
				onExport={handleExport}
				clearSelection={() => {
					setSelectedItems([]);
					setSelectAll(false);
					setSelectType(ContactSelectType.CURRENT_PAGE);
				}}
			/>

			<AddToCrmModal
				isLoading={isAddToCrmLoading}
				isOpen={!!selectedCrmProvider}
				provider={selectedCrmProvider}
				selectedCount={selectionCount}
				onClose={() => setSelectedCrmProvider(undefined)}
				onSubmit={handleSubmitAddToCrm}
			/>

			{isSmartListActive && showSmartListModal && (
				<CreateSmartListModal
					filterCount={filterCount}
					isLoading={isCreateSmartListLoading}
					isOpen={showSmartListModal}
					semanticSearchQuery={semanticSearchProspects}
					onClose={() => setShowSmartListModal(false)}
					onSubmit={handleCreateSmartList}
				/>
			)}

			{addToListConfirm.modal}
			{lookups.modal}

			{showAddToTrackerModal && (
				<Modal
					handleClose={() => {
						setShowAddToTrackerModal(false);
						setSingleTrackerProspectId(null);
						setSelectedTracker(undefined);
						setAddOrganizationOnly(false);
					}}
					handleSave={handleConfirmAddToTracker}
					isLoading={isAddToTrackerLoading}
					isOpened={showAddToTrackerModal}
					disableSave={!selectedTracker}
					size="md"
					submitButtonText={t("basics.confirm")}
					title={ts("addToTrackerTitle")}
				>
					<div className="mb-4 flex flex-row items-center">
						<AutoComplete
							data={combinedTrackerAndJobChangeLists.map(
								(list) =>
									({
										id: list.id,
										title: list.name,
										rightContent: renderListMeta(list),
									}) as AutoCompleteItem
							)}
							handleSearch={(value: string) => setTrackerSearchValue(value)}
							handleSelect={(value?: AutoCompleteItem) => setSelectedTracker(value)}
							icon={faMegaphone}
							isLoading={isLoadingTrackerLists || isFetchingTrackerLists}
							label={"Custom Signal Tracker or Job Change List"}
							placeholder={"Search for a Custom Signal Tracker or Job Change list"}
							selectedItem={selectedTracker?.title || ""}
						/>
					</div>
					{!selectedListIsJobChange && (
						<div className="mb-4">
							<Checkbox
								isChecked={addOrganizationOnly}
								labelClass="cursor-pointer"
								onChange={() => setAddOrganizationOnly(!addOrganizationOnly)}
							>
								{t("inbox.addOrganizationOnly")}
							</Checkbox>
						</div>
					)}
				</Modal>
			)}

			<BlacklistConfirmModal
				addEntireCompany={addEntireCompanyToBlacklist}
				isConfirmLoading={isAddToBlacklistLoading}
				isOpened={showBlacklistModal}
				selectedCount={selectionCount}
				selectionPayload={getSelectionPayload()}
				onClose={() => {
					setShowBlacklistModal(false);
					setAddEntireCompanyToBlacklist(false);
				}}
				onConfirm={handleAddToBlacklist}
				onToggleEntireCompany={setAddEntireCompanyToBlacklist}
			/>
			{/* impact preview + confirmation live in the extracted component */}

			{renderSnackbar()}
			{renderMessageSnackbar()}
		</div>
	);
};
